import {
  useFocusEffect,
  useNavigation,
  CommonActions,
} from '@react-navigation/native';
import { StackNavigationProp } from '@react-navigation/stack';
import React, { useCallback, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { TouchableOpacity } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

import RestyleIcon from '@components/shared/Icon/RestyleIcon';
import { Chat } from '@graphql/generated';
import { useApolloClient } from '@hooks/useApolloClient';
import useChatAvatar from '@hooks/useChatAvatar';
import useChatInput from '@hooks/useChatInput';
import { useGetChatFromCache } from '@hooks/useGetChatFromCache';
import useGlobalSearch from '@hooks/useGlobalSearch';
import useMe from '@hooks/useMe';
import useSearch from '@hooks/useSearch';
import { ChatsStackParamList } from '@navigation/chats/chats-stack';
import theme from '@themes/theme';

import Avatar from '../Avatar/Avatar';
import { EmailAvatar } from '../Avatar/EmailAvatar';
import { Box, Text } from '../Restyle';
import Icon from '../shared/Icon/Icon';
import { SearchInputWithTags } from '../shared/SearchInput/SearchInputWithTags';

type ChatHeaderProps = {
  chatId: Chat['id'];
  friendRequestAccepted?: boolean;
};

const ChatHeader: React.FC<ChatHeaderProps> = ({
  chatId: id,
  friendRequestAccepted = true,
}) => {
  const { t } = useTranslation('models');
  const insets = useSafeAreaInsets();
  const navigation =
    useNavigation<StackNavigationProp<ChatsStackParamList, 'chat-detail'>>();
  const { isSearching, setIsSearching, isSearchPreview } = useSearch();
  const { client } = useApolloClient();

  const { chat } = useGetChatFromCache(id);

  const {
    id: chatId,
    isGroupChat,
    name,
    settings,
    users = [],
    avatarUrl,
  } = chat || {};

  const { me } = useMe();

  const otherUser = useMemo(
    () =>
      !isGroupChat && users ? users.find((i) => i.id !== me?.id) : undefined,
    [isGroupChat, users, me]
  );
  const isSelfChat = !isGroupChat && !!me?.id && !!users?.length && !otherUser;
  const groupName = name?.trim();

  const getAvatarId = () => {
    if (isSelfChat) return me?.id;
    if (isGroupChat) return chatId;
    return otherUser?.id;
  };
  const isChatPendingRequest =
    chat?.isPendingRequest && !chat?.users?.some((user) => user.id === me?.id);

  const isArchived = settings?.archivedAt;
  const { isSearching: isGlobalSearching } = useGlobalSearch();

  const { getChatAvatarLabel } = useChatAvatar();
  const { subscribeToChatUpdates } = useChatInput();

  useEffect(() => {
    return () => {
      setIsSearching(false);
    };
  }, []);

  useFocusEffect(
    useCallback(() => {
      // Subscribe to chat
      const unsubscribe = subscribeToChatUpdates?.();

      // Unsubscribe from chat updates when the screen is blurred
      return () => {
        unsubscribe?.();
      };
    }, [client])
  );

  const isSenderFriendRequest = friendRequestAccepted === false && !isGroupChat;
  const isPendingInviteChat =
    !isGroupChat &&
    (chat?.users?.length ?? 0) === 1 &&
    chat?.pendingInvitations?.length === 1 &&
    chat?.pendingInvitations[0].email === chat?.name;

  return (
    <Box
      paddingHorizontal='m'
      paddingBottom={isSearching ? 'none' : 'xs'}
      borderBottomColor='grey02'
      borderBottomWidth={1}
      flexDirection='row'
      alignItems='center'
      marginTop='xs'
      style={{ paddingTop: insets.top }}>
      {!isSearching ? (
        <>
          <Box>
            <TouchableOpacity
              onPress={() => {
                if (isGlobalSearching) {
                  navigation.pop();
                } else if (navigation.canGoBack()) {
                  navigation.goBack();
                } else {
                  navigation.dispatch(
                    CommonActions.reset({
                      index: 0,
                      routes: [{ name: 'home' }],
                    })
                  );
                }
              }}
              hitSlop={{ top: 10, bottom: 10, right: 10, left: 10 }}>
              <RestyleIcon
                name='ChevronLeft'
                variant='xl'
                color='textPrimary'
                //marginRight='xs'
              />
            </TouchableOpacity>
          </Box>
          <Box marginLeft='xs'>
            {isPendingInviteChat ? (
              <EmailAvatar width={40} />
            ) : (
              React.createElement(Avatar as any, {
                avatarType: isSelfChat ? 'user' : undefined,
                id: getAvatarId(),
                size: 'small',
                isMultiple: isGroupChat,
                avatarUrl: avatarUrl,
                label: name && !isGroupChat ? name : getChatAvatarLabel(chat),
                skipCache: true,
                disableAvatarPress: isGroupChat || isSenderFriendRequest,
                backgroundColor: isChatPendingRequest
                  ? theme.colors.grey02
                  : undefined,
                isSenderFriendRequest: isChatPendingRequest
                  ? false
                  : isSenderFriendRequest,
                groupColorSeed:
                  isGroupChat && groupName ? groupName : undefined,
              })
            )}
          </Box>
          <Box flex={1} marginHorizontal='xs'>
            <TouchableOpacity
              onPress={() => {
                if (!isGroupChat || isChatPendingRequest === false) {
                  navigation.navigate(
                    isGroupChat
                      ? 'group-chat-settings'
                      : 'individual-chat-settings',
                    { chatId: id }
                  );
                }
              }}
              accessibilityLabel={isGroupChat ? 'Group Info' : 'Chat Info'}>
              <Text
                variant='labelEmphasized'
                color='textPrimary'
                numberOfLines={1}>
                {name || getChatAvatarLabel(chat) || ' '}
              </Text>
              {isSearchPreview && (
                <Text variant='metadataSecondary' color='textSecondary'>
                  Chat history search
                </Text>
              )}
              {!isSearchPreview && isArchived && (
                <Text variant='metadataSecondary' color='grey04'>
                  {t('shared:archived')}
                </Text>
              )}
              {!isSearchPreview &&
                !isArchived &&
                (!isGroupChat || isChatPendingRequest === false) && (
                  <Text variant='metadataSecondary' color='textSecondary'>
                    {isGroupChat
                      ? t('chat.labels.groupChatSettingsButton')
                      : t('chat.labels.individualChatSettingsButton') || ' '}
                  </Text>
                )}
            </TouchableOpacity>
          </Box>
          {!isSearchPreview &&
            (!isGroupChat || isChatPendingRequest === false) && (
              <Icon
                name='Search'
                variant='m'
                color='textPrimary'
                onPress={() => setIsSearching(true)}
                accessibilityLabel='Search'
              />
            )}
        </>
      ) : (
        <SearchInputWithTags showCancel autofocus />
      )}
    </Box>
  );
};

export default ChatHeader;
